import React, {
    ReactElement,
    ReactNode,
    useCallback,
    useMemo,
    useState,
} from "react";
import { Form } from "react-bootstrap";
import {
    ActionContextType, GetSettingsDocument, useGetWorkflowByReferenceLazyQuery, useSaveSettingsMutation, Workflow,
} from "../../../../../__generated__/api";
import EditorV3 from "../../../../../components/agent-v3/editor/Editor";
import CodeBlock from "../../../../../components/code-block/CodeBlock";
import useDirtyTracker from "../../../../../components/dirty-tracker/DirtyTracker";
import { useFormBuilder } from "../../../../../components/dirty-tracker/FormTracker";
import Editor, { DEFAULT_TEXT } from "../../../../../components/editor/Editor";
import Help, { ARTICLES } from "../../../../../components/help/Help";
import IconUploader from "../../../../../components/pickers/icon-uploader/IconUploader";
import TagList from "../../../../../components/tag-list/TagList";
import FormGroup from "../../../../../components/theme/form-group/FormGroup";
import SettingsContainer from "../../../../../components/theme/settings";
import WorkflowPicker from "../../../../../components/workflow/WorkflowPicker";
import useArticleContext from "../../../../../contexts/HelpContext";
import { SettingsProps } from "../../base";
import Styles from "./WebChat.module.scss";
import Button from "../../../../../components/theme/button/Button";
import { useAutomationFlow } from "../../../../../components/workflow-v3/AutomationFlow";

export function ChannelSettingsV3(props: SettingsProps): ReactNode {
    const [save] = useSaveSettingsMutation();
    const [loadReference, { data: referenceData }] = useGetWorkflowByReferenceLazyQuery();

    const { tracker, properties } = useFormBuilder(
        builder => builder
            .property("name", () => props.settings?.channelWebChat?.name ?? "")
            .array("allowedHosts", () => props.settings?.channelWebChat?.allowedHosts ?? [])
            .property("avatarUrl", () => props.settings?.channelWebChat?.avatarUrl)
            .property("welcomeMessage", () => props.settings?.channelWebChat?.welcomeMessage ?? ""),
        [props.settings?.channelWebChat],
    );

    const performSave = useCallback(async () => {
        await save({
            refetchQueries: [
                { query: GetSettingsDocument },
            ],
            variables: {
                channelWebChat: {
                    allowedHosts: properties.allowedHosts.value,
                    avatarUrl: properties.avatarUrl.value,
                    name: properties.name.value ?? "",
                    welcomeMessage: properties.welcomeMessage.value,
                    isReferrerCheckRequired: properties.allowedHosts.value.isEmpty,
                },
            },
        });
    }, [save, properties]);

    const { element: saveElement } = tracker.useDirtyTracker({
        finishedSavingMessage: "Web settings successfully saved.",
        onSave: async (__, onDone) => {
            await performSave();
            onDone();
        },
    });

    const workflow = useMemo(() => referenceData?.workflowReference?.workflow, [referenceData]);

    const { element: automationElement, setShouldShow: setShowAutomation } = useAutomationFlow({
        workflow: workflow as Workflow | undefined,
        title: "User automation",
    });

    const handleShowAutomation = useCallback(async () => {
        await loadReference({
            variables: {
                referenceId: props.settings?.channelWebChat?.id,
            },
        });
        setShowAutomation(true);
    }, [loadReference, props.settings?.channelWebChat?.id]);

    return (
        <>
            <SettingsContainer>
                <SettingsContainer.Section
                    title="General"
                    helpText="General chat settings manage all the settings to help you personalize and get started with chat."
                >
                    <SettingsContainer.Setting
                        title="Avatar"
                        description="The image to use as the avatar image when showing up for support agent."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                    >
                        <IconUploader
                            value={properties.avatarUrl.value as string}
                            onChange={icon => properties.avatarUrl.setValue(icon)}
                            shouldStore
                        />
                    </SettingsContainer.Setting>
                    <SettingsContainer.Setting
                        title="Chat Name"
                        description="The name of the chat configuration."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                    >
                        <FormGroup
                            type="text"
                            placeholder="Configuration Name"
                            required
                            size="small"
                            onChange={evt => properties.name.setValue(evt.target.value)}
                            value={properties.name.value}
                        />
                    </SettingsContainer.Setting>
                    <SettingsContainer.Setting
                        title="Welcome Message"
                        description="The default message a user sees when they open the chat."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                    >
                        <EditorV3
                            value={properties.welcomeMessage.value ?? ""}
                            size="medium"
                            hasBorder
                            onChange={properties.welcomeMessage.setValue}
                            placeholder="Hey, 👋 give us a few seconds while we get you an answer.
                        Please add any additional questions to this thread."
                        />
                    </SettingsContainer.Setting>
                    <SettingsContainer.Setting
                        title="Allowed Hosts"
                        description="Which websites or hostnames should the chat be allowed to be served from?"
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                    >
                        <TagList
                            value={properties.allowedHosts.value}
                            onChange={properties.allowedHosts.setValue}
                            canNotHaveDuplicates
                            placeholder="Add host"
                            type="url"
                        />
                    </SettingsContainer.Setting>
                </SettingsContainer.Section>
                <SettingsContainer.Section
                    title="Web Script"
                >
                    <SettingsContainer.Setting
                        title="Your web script"
                        description="This is an automatically generated script which, when added to your website,
                        will display a chat widget in the bottom right hand corner. Copy this javascript
                        block to your web page where you want to include chat."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                    >
                        <CodeBlock shouldWrap code={props.settings?.channelWebChat?.chatScript} />
                    </SettingsContainer.Setting>

                    <SettingsContainer.Setting
                        title="Public User Signing Key"
                        description="You can use this key to generate a user identity key. Please
                    view the following help article to better understand how to generate a user token.
                    How to generate a user token?"
                        descriptionElement={(
                            <>
                                You can use this key to generate a user identity key. Please view the
                                {" "}
                                <Help
                                    articleId={ARTICLES.WEB_CHAT_USER_AUTHENTICATION}
                                    buttonClassName={Styles["help-link"]}
                                    button="following help article"
                                />
                                {" "}
                                to better understand how to generate a user token.
                            </>
                        )}
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                    >
                        <CodeBlock shouldWrap code={props.settings?.channelWebChat?.publicSigningKey} />
                    </SettingsContainer.Setting>

                </SettingsContainer.Section>
                <SettingsContainer.Section
                    title="Automation"
                >
                    <SettingsContainer.Setting
                        title="Automation"
                        description=" This automation automatically runs once the user is connected and verified. You can automate
                        routing requests, sending the user questions, and sending question deflection."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                    >
                        <Button.Add
                            size="small"
                            onClick={handleShowAutomation}
                        >
                            Edit Automation
                        </Button.Add>
                        {/* <WorkflowPicker
                            buttonClassName="text-light"
                            iconClassName="text-light"
                            referenceId={props.settings?.channelWebChat?.id}
                            title="User automation"
                            buttonText="Edit Automation"
                            type={ActionContextType.Ticket}
                        /> */}
                    </SettingsContainer.Setting>
                </SettingsContainer.Section>
            </SettingsContainer>
            {saveElement}
            {automationElement}
        </>
    );
}

export default function ChannelSettings(props: SettingsProps): ReactElement {
    useArticleContext("urn:fq:article:c1901c253fef4f11913f5c9fabde610a");

    const [save] = useSaveSettingsMutation();

    const [allowedHosts, setAllowedHosts] = useState(() => props.settings?.channelWebChat?.allowedHosts ?? []);
    const [avatarUrl, setAvatarUrl] = useState(() => props.settings?.channelWebChat?.avatarUrl);
    const [name, setName] = useState(() => props.settings?.channelWebChat?.name);
    const [welcomeMessage, setWelcomeMessage] = useState(() => props.settings?.channelWebChat?.welcomeMessage);

    const dirtyTracker = useDirtyTracker({
        finishedSavingMessage: "Web settings saved.",
        onSave: (event, onDone) => {
            event.preventDefault();
            (async () => {
                await save({
                    refetchQueries: [
                        { query: GetSettingsDocument },
                    ],
                    variables: {
                        channelWebChat: {
                            allowedHosts,
                            avatarUrl,
                            name: name ?? "",
                            welcomeMessage,
                            isReferrerCheckRequired: allowedHosts.isEmpty,
                        },
                    },
                });
                onDone();
            })();
        },
    });

    dirtyTracker.useWatchProperty(allowedHosts, props.settings?.channelWebChat?.allowedHosts ?? []);
    dirtyTracker.useWatchDeepCompareProperty(avatarUrl, props?.settings?.channelWebChat?.avatarUrl);
    dirtyTracker.useWatchProperty(name, props?.settings?.channelWebChat?.name);
    dirtyTracker.useWatchProperty(
        welcomeMessage ?? DEFAULT_TEXT,
        props?.settings?.channelWebChat?.welcomeMessage ?? DEFAULT_TEXT,
    );

    dirtyTracker.useLoad(() => {
        setAllowedHosts(props.settings?.channelWebChat?.allowedHosts ?? []);
        setAvatarUrl(props?.settings?.channelWebChat?.avatarUrl);
        setName(props?.settings?.channelWebChat?.name);
        setWelcomeMessage(props?.settings?.channelWebChat?.welcomeMessage);
    }, [props?.settings]);

    return (
        <form className="flex-grow-1">

            <div className="border p-3 rounded mb-5 bg-white">
                <h5 className="mb-3 text-light">
                    Chat Settings
                </h5>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder d-block mb-0">Avatar</Form.Label>
                    <Form.Text className="mb-2 d-block">
                        The image to use as the avatar image when showing up for support agent.
                    </Form.Text>
                    <IconUploader value={avatarUrl as string} onChange={icon => setAvatarUrl(icon)} shouldStore />

                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Name</Form.Label>
                    <Form.Control
                        value={name}
                        onChange={value => setName(value.target.value)}
                        type="text"
                        placeholder="Configuration name"
                    />
                    <Form.Text>
                        The name of the chat configuration.
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Welcome Message</Form.Label>

                    <Editor
                        className="border border-gray rounded"
                        text={welcomeMessage ?? ""}
                        onChange={msg => setWelcomeMessage(msg)}
                        placeholder="Hello and welcome!"
                    />
                    <Form.Text>
                        The default message a user sees when they open the chat.
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Allowed Hosts</Form.Label>
                    <TagList
                        value={allowedHosts}
                        onChange={value => setAllowedHosts(value)}
                        canNotHaveDuplicates
                        placeholder="Add host"
                        type="url"
                    />
                    <Form.Text>
                        Which websites or hostnames should the chat be allowed to be served from?
                    </Form.Text>
                </Form.Group>

            </div>

            <div className="border p-3 rounded bg-white mb-4">
                <div>
                    Your web script
                    <CodeBlock shouldWrap code={props.settings?.channelWebChat?.chatScript} className="my-2" />
                    <small>
                        This is an automatically generated script which, when added to your website, will display
                        a chat widget in the bottom right hand corner. Copy this javascript block
                        to your web page where you want to include chat.
                    </small>
                </div>
            </div>

            <div className="border p-3 rounded bg-white mb-4">
                <div>
                    Public User Signing Key
                    <CodeBlock shouldWrap code={props.settings?.channelWebChat?.publicSigningKey} className="my-2" />
                    <small>
                        You can use this key to generate a user identity key. Please view the following help article
                        to better understand how to generate a user token.
                        {" "}
                        <Help
                            articleId={ARTICLES.WEB_CHAT_USER_AUTHENTICATION}
                            buttonClassName="btn btn-link"
                            button="How to generate a user token"
                        />
                    </small>
                </div>
            </div>

            <div className="border p-3 rounded bg-white mb-4">
                <div>
                    Automation
                    {" "}

                    <WorkflowPicker
                        buttonClassName="text-light"
                        iconClassName="text-light"
                        referenceId={props.settings?.channelWebChat?.id}
                        title="User automation"
                        buttonText="Edit Automation"
                        type={ActionContextType.Ticket}
                    />

                </div>
                <small>
                    This automation automatically runs once the user is connected and verified. You can automate
                    routing requests, sending the user questions, and sending question deflection.
                </small>
            </div>

            {dirtyTracker.saveComponent}

        </form>
    );
}
